/**
 * Reading the session mirror from React (U-08-f / D10).
 *
 * `api/session.ts` owns the state; these two hooks are the only way a component touches it, and
 * they read a snapshot rather than calling a door — a page that re-fetched `permissions/effective`
 * on every render would be polling the server for a fact the console is not allowed to decide.
 *
 * The one rule worth stating next to the code: `useCapability` answers FOUR things, not three.
 * `unknown` means «نمی‌دانم» (the mirror has not arrived) and `none` means «نداری» (the server
 * said so). Rendering them alike is how a console quietly denies a page to an operator who could
 * have seen it, which is the U-06-e lesson in the other direction.
 */
import { useSyncExternalStore } from 'react';
import { getSnapshot, subscribe, type SessionSnapshot } from '../api/session';
import type { CapabilityLevel } from '../api/types';

export function useSessionSnapshot(): SessionSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * The caller's own level for one capability key, exactly as
 * `GET /ops/v1/permissions/effective` published it (§۹: the console mirrors, it does not judge).
 */
export function useCapability(key: string): CapabilityLevel {
  const snapshot = useSessionSnapshot();
  if (!snapshot.levelsKnown || snapshot.levels === null) return 'unknown';
  return snapshot.levels[key] ?? 'none';
}
